import React, { Component } from "react";
import CESSVG from "../../Assets/svg/CESSVG.svg";
import { LogoutAction } from "../../Actions/Authentication";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { withRouterHooks } from "../../withRouters/withRoutersHook";
import { FetchAllMessagesForOverview } from "../../Actions/Messaging";
import { Link } from "react-router-dom";
import {
  BarChart2,
  Bot,
  Files,
  LogOut,
  Mail,
  FileText,
  Settings,
  Workflow,
  ClipboardList,
} from "lucide-react";

class DashboardSidebar extends Component {
  static propTypes = {
    LogoutAction: PropTypes.func.isRequired,
    navigate: PropTypes.func.isRequired,
    FetchAllMessagesForOverview: PropTypes.func.isRequired,
    allOverviewChatSessionsTotal: PropTypes.number,
    location: PropTypes.object.isRequired,
    currentUser: PropTypes.object,
  };

  componentDidMount() {
    this.props.FetchAllMessagesForOverview();
  }

  getActivePath = () => {
    const { pathname } = this.props.location;
    if (pathname === "/dashboard" || pathname.includes("/dashboard/result-summary")) return "dashboard";
    if (pathname.includes("/dashboard/pdf-analyzer")) return "pdf-analyzer";
    if (pathname.includes("/dashboard/smartflow")) return "smartflow";
    if (pathname.includes("/dashboard/file-manager")) return "file-manager";
    if (pathname.includes("/dashboard/ai-chatbot")) return "ai-chatbot";
    if (pathname.includes("/dashboard/message-overview")) return "messages";
    if (pathname.includes("/dashboard/analytics")) return "analytics";
    if (pathname.includes("/dashboard/settings")) return "settings";
    return null;
  };

  handleLogout = () => {
    this.props.LogoutAction();
  };

  isCX2ChatDisabled = () => {
    const { currentUser } = this.props;

    // Debug: Log the user object to see what we have
    console.log('CX2 Chat Access Check:', {
      currentUser,
      is_staff: currentUser?.is_staff,
      is_superuser: currentUser?.is_superuser,
      is_prime_superuser: currentUser?.is_prime_superuser,
      is_app_admin: currentUser?.is_app_admin,
      is_app_superuser: currentUser?.is_app_superuser,
      department: currentUser?.department,
      active_departments: currentUser?.active_departments,
    });

    if (!currentUser) return true;

    // Check if user is admin-level (staff, superuser, prime superuser, app admin, or app superuser)
    const isAdminLevel =
      currentUser.is_staff ||
      currentUser.is_superuser ||
      currentUser.is_prime_superuser ||
      currentUser.is_app_admin ||
      currentUser.is_app_superuser;

    console.log('Is Admin Level:', isAdminLevel);

    // If admin-level user, always allow access
    if (isAdminLevel) return false;

    // For normal users, check if CX2BOT access has been explicitly granted
    if (!currentUser.has_access_to_ai) return true;

    // For normal users, check if they have any active departments
    const hasActiveDepartments =
      currentUser.active_departments &&
      Array.isArray(currentUser.active_departments) &&
      currentUser.active_departments.length > 0;

    // Also check legacy department field as fallback
    const hasLegacyDepartment =
      currentUser.department &&
      currentUser.department !== null &&
      currentUser.department !== "";

    const disabled = !(hasActiveDepartments || hasLegacyDepartment);
    console.log('CX2 Chat Disabled:', disabled);

    // Disable if normal user without any departments
    return disabled;
  };

  renderNavItem = (to, key, icon, label, badge = null, disabled = false) => {
    const active = this.getActivePath() === key;
    const content = (
      <div
        className={`flex items-center gap-3 px-3 py-2.5 rounded-lg font-inter text-sm font-normal tracking-wide transition-all duration-150 ${
          disabled
            ? "cursor-not-allowed opacity-40"
            : active
            ? "bg-gradient-to-r from-primary-blue to-blue-400 text-white shadow-md cursor-pointer"
            : "text-gray-400 hover:bg-gradient-to-r hover:from-white/10 hover:to-white/5 hover:text-white cursor-pointer"
        }`}
      >
        {React.cloneElement(icon, {
          className: `w-[1.125rem] h-[1.125rem] shrink-0 flex-none ${
            disabled ? "text-gray-600" : active ? "text-white" : "text-gray-400"
          }`,
        })}
        <span className="flex-1 whitespace-nowrap overflow-hidden">{label}</span>
        {badge !== null && (
          <span
            className={`text-xs font-semibold px-2 py-0.5 rounded-full flex-none ${
              disabled
                ? "bg-gray-600 text-gray-400"
                : active
                ? "bg-white/20 text-white"
                : "bg-primary-blue text-white"
            }`}
          >
            {badge}
          </span>
        )}
      </div>
    );

    if (disabled) {
      return (
        <div key={key} title="Available for users with assigned departments">
          {content}
        </div>
      );
    }

    return (
      <Link to={to} key={key}>
        {content}
      </Link>
    );
  };

  render() {
    const messageCount = this.props.allOverviewChatSessionsTotal || null;

    return (
      <div className="group flex flex-col w-14 hover:w-[16.625rem] h-screen bg-[#0f1f3d] z-30 transition-[width] duration-200 ease-in-out overflow-hidden shadow-none hover:shadow-2xl">
        {/* Logo */}
        <div className="flex items-center gap-3 py-5 px-3.5 border-b border-white/5 overflow-hidden">
          <div className="w-7 h-7 shrink-0 bg-white rounded-md flex items-center justify-center">
            <img src={CESSVG} alt="CES" className="w-5 h-5" />
          </div>
          <span className="font-SFPro font-semibold text-white text-sm whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-150 delay-75">
            CX2 Platform
          </span>
        </div>

        {/* Nav items */}
        <nav className="flex-1 overflow-y-auto overflow-x-hidden px-2 py-3 flex flex-col gap-0.5">
          {this.renderNavItem("/dashboard", "dashboard", <ClipboardList />, "SmartSurvey")}
          {this.renderNavItem("/dashboard/analytics", "analytics", <BarChart2 />, "Analytics")}
          {this.renderNavItem("/dashboard/pdf-analyzer", "pdf-analyzer", <FileText />, "SmartExtract")}
          {this.renderNavItem("/dashboard/smartflow", "smartflow", <Workflow />, "SmartFlow")}
          {this.renderNavItem("/dashboard/file-manager", "file-manager", <Files />, "All Files")}
          {this.renderNavItem("/dashboard/ai-chatbot", "ai-chatbot", <Bot />, "CX2", null, this.isCX2ChatDisabled())}
          {this.renderNavItem(
            "/dashboard/message-overview",
            "messages",
            <Mail />,
            "Messages",
            messageCount
          )}
          {this.renderNavItem("/dashboard/settings", "settings", <Settings />, "Settings")}
        </nav>

        {/* Logout */}
        <div className="px-2 py-3 border-t border-white/5">
          <button
            onClick={this.handleLogout}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg font-inter text-sm font-medium text-gray-400 hover:bg-red-500/10 hover:text-red-400 transition-all duration-150"
          >
            <LogOut className="w-[1.125rem] h-[1.125rem] shrink-0 flex-none" />
            <span className="whitespace-nowrap overflow-hidden">Logout</span>
          </button>
        </div>
      </div>
    );
  }
}

const mapStateToProps = (state) => ({
  allOverviewChatSessionsTotal: state.messaging.allOverviewChatSessionsTotal,
  currentUser: state.authentication.user,
});

export default withRouterHooks(
  connect(mapStateToProps, { LogoutAction, FetchAllMessagesForOverview })(
    DashboardSidebar
  )
);
